console.log("===============================");
console.log("=== SERVER IS BOOTING UP... ===");
console.log("===============================");

import './config/env'; // validate env first
import express from 'express';
import http from 'http';
import { Server as SocketIOServer } from 'socket.io';
import helmet from 'helmet';
import cors from 'cors';
import compression from 'compression';
import morgan from 'morgan';
import rateLimit from 'express-rate-limit';

import { env } from './config/env';
import { prisma } from './shared/prisma';
import { redis } from './shared/redis';
import { errorHandler } from './shared/errors/AppError';
import { registerSocketHandlers } from './realtime/socket.bootstrap';

// Routers
import authRouter from './modules/auth/auth.router';
import usersRouter from './modules/users/users.router';
import teamsRouter from './modules/teams/teams.router';
import channelsRouter from './modules/channels/channels.router';
import messagesRouter from './modules/messages/messages.router';
import conversationsRouter from './modules/conversations/conversations.router';
import notificationsRouter from './modules/notifications/notifications.router';
import adminRouter from './modules/admin/admin.router';
import settingsRouter from './modules/settings/settings.router';
import uploadsRouter from './modules/uploads/uploads.router';
import livekitRouter from './modules/livekit/livekit.router';
import path from 'path';
import fs from 'fs';

const app = express();
const httpServer = http.createServer(app);


// ─── Socket.IO ────────────────────────────────
const io = new SocketIOServer(httpServer, {
  cors: { origin: (origin, callback) => callback(null, origin || true), credentials: true },
  pingTimeout: 10000,
  pingInterval: 5000,
  maxHttpBufferSize: 5e8 // 500MB limit for file attachments via websocket
});
registerSocketHandlers(io);

// ─── Middleware ───────────────────────────────
app.use(helmet({ crossOriginResourcePolicy: { policy: "cross-origin" } })); // Allow cross-origin images
app.use(cors({ origin: (origin, callback) => callback(null, origin || true), credentials: true }));
app.use(compression());
app.use(express.json({ limit: '500mb' }));
app.use(express.urlencoded({ extended: true }));
app.use(morgan(env.NODE_ENV === 'development' ? 'dev' : 'combined'));

// Serve static files
app.use('/uploads', express.static(path.join(process.cwd(), 'uploads'), {
  setHeaders: (res, filePath) => {
    if (filePath.endsWith('.mp3')) {
      res.setHeader('Content-Type', 'audio/mpeg');
      res.setHeader('Accept-Ranges', 'bytes');
    } else if (filePath.endsWith('.mp4')) {
      res.setHeader('Content-Type', 'video/mp4');
      res.setHeader('Accept-Ranges', 'bytes');
    } else if (filePath.endsWith('.wav')) {
      res.setHeader('Content-Type', 'audio/wav');
      res.setHeader('Accept-Ranges', 'bytes');
    } else if (filePath.includes('call_recordings') || filePath.endsWith('.webm')) {
      res.setHeader('Content-Type', 'audio/webm');
      res.setHeader('Accept-Ranges', 'bytes');
    }
  }
}));
app.use('/updates', express.static(path.join(process.cwd(), 'updates')));

// Rate limiting
app.set('trust proxy', 1); // IMPORTANT: Trust Nginx proxy so rate limiter uses actual client IP

// PERMANENT FIX: Set to 5000 requests per 1 minute (60,000 ms) as requested
const limiter = rateLimit({ windowMs: 1 * 60 * 1000, max: 5000, standardHeaders: true, legacyHeaders: false });
app.use('/api', limiter);

// Keep a very high limit only on login/auth routes to prevent basic brute-force hacking
const authLimiter = rateLimit({ windowMs: 15 * 60 * 1000, max: 10000, message: { success: false, message: 'Too many auth attempts' } });
app.use('/api/auth', authLimiter);

// ─── Routes ───────────────────────────────────
app.use('/api/auth', authRouter);
app.use('/api/users', usersRouter);
app.use('/api/teams', teamsRouter);
app.use('/api/teams/:teamId/channels', channelsRouter);
app.use('/api/channels', channelsRouter);
app.use('/api/messages', messagesRouter);
app.use('/api/conversations', conversationsRouter);
app.use('/api/notifications', notificationsRouter);
app.use('/api/admin', adminRouter);
app.use('/api/settings', settingsRouter);
app.use('/api/upload', uploadsRouter);
app.use('/api/livekit', livekitRouter);

// Health check
app.get('/health', (_req, res) => res.json({ status: 'ok', timestamp: new Date().toISOString() }));
app.get('/api/health', (_req, res) => res.json({ status: 'ok', timestamp: new Date().toISOString() }));

// 404
app.use((_req, res) => res.status(404).json({ success: false, message: 'Not found' }));

// Error handler (must be last)
app.use(errorHandler);

// ─── File Cleanup Job (15 Days) ───────────────
/*
setInterval(async () => {
  try {
    const fifteenDaysAgo = new Date(Date.now() - 15 * 24 * 60 * 60 * 1000);
    const oldAttachments = await prisma.attachment.findMany({
      where: { createdAt: { lt: fifteenDaysAgo } }
    });
    
    for (const att of oldAttachments) {
      const filePath = path.join(process.cwd(), att.storageUrl);
      if (fs.existsSync(filePath)) {
        fs.unlinkSync(filePath);
      }
    }
    
    await prisma.attachment.deleteMany({
      where: { createdAt: { lt: fifteenDaysAgo } }
    });
    if (oldAttachments.length > 0) {
      console.log(`[Cleanup] Deleted ${oldAttachments.length} old attachments.`);
    }
  } catch (err) {
    console.error('[Cleanup] Error cleaning up old attachments', err);
  }
}, 24 * 60 * 60 * 1000); // Run once a day
*/

// ─── Startup ──────────────────────────────────
async function bootstrap() {
  try {
    await prisma.$connect();
    console.log('✅ PostgreSQL connected');

    // Auto-verify / create call_logs table in live database
    try {
      await prisma.$executeRawUnsafe(`
        CREATE TABLE IF NOT EXISTS "call_logs" (
          "id" TEXT NOT NULL,
          "callerId" TEXT NOT NULL,
          "receiverId" TEXT NOT NULL,
          "callType" TEXT NOT NULL DEFAULT 'audio',
          "status" TEXT NOT NULL DEFAULT 'completed',
          "recordingUrl" TEXT,
          "startedAt" TIMESTAMP(3) NOT NULL DEFAULT CURRENT_TIMESTAMP,
          "connectedAt" TIMESTAMP(3),
          "endedAt" TIMESTAMP(3),
          "duration" INTEGER NOT NULL DEFAULT 0,
          "roomName" TEXT,
          "metadata" JSONB NOT NULL DEFAULT '{}'::jsonb,
          "createdAt" TIMESTAMP(3) NOT NULL DEFAULT CURRENT_TIMESTAMP,
          CONSTRAINT "call_logs_pkey" PRIMARY KEY ("id"),
          CONSTRAINT "call_logs_callerId_fkey" FOREIGN KEY ("callerId") REFERENCES "users"("id") ON DELETE CASCADE ON UPDATE CASCADE,
          CONSTRAINT "call_logs_receiverId_fkey" FOREIGN KEY ("receiverId") REFERENCES "users"("id") ON DELETE CASCADE ON UPDATE CASCADE
        );
      `);
      await prisma.$executeRawUnsafe(`CREATE INDEX IF NOT EXISTS "call_logs_callerId_createdAt_idx" ON "call_logs"("callerId", "createdAt");`);
      await prisma.$executeRawUnsafe(`CREATE INDEX IF NOT EXISTS "call_logs_receiverId_createdAt_idx" ON "call_logs"("receiverId", "createdAt");`);
      await prisma.$executeRawUnsafe(`CREATE INDEX IF NOT EXISTS "call_logs_createdAt_idx" ON "call_logs"("createdAt");`);
      console.log('✅ PostgreSQL call_logs table verified/created');
    } catch (dbInitErr) {
      console.warn('⚠️ Note on call_logs table initialization:', dbInitErr);
    }

    await redis.connect();

    const port = Number(env.PORT);
    httpServer.listen(port, () => {
      console.log(`\n🚀 iChat Server running on http://localhost:${port}`);
      console.log(`📡 Socket.IO ready`);
      console.log(`🌍 Environment: ${env.NODE_ENV}\n`);
    });
  } catch (err) {
    console.error('❌ Failed to start server:', err);
    process.exit(1);
  }
}

bootstrap();

// Graceful shutdown
process.on('SIGTERM', async () => {
  console.log('SIGTERM received, shutting down gracefully...');
  await prisma.$disconnect();
  await redis.quit();
  process.exit(0);
});
